import { createStyles } from 'antd-style';

export const useAppLayoutStyles = createStyles(({ css, token }) => ({
  brandMark: css`
    display: block;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    background: url('${import.meta.env.BASE_URL}brand-logo.png') center / contain no-repeat;
  `,
  layoutRoot: css`
    height: 100dvh;
    min-height: 0;
    overflow: hidden;

    /* Without a sider, Ant Layout switches to a column; the content container
       must grow vertically as well as shrink around the fixed shell regions. */
    .ant-pro-layout-container {
      flex: 1 1 0;
      min-height: 0;
      min-width: 0;
    }

    /* 内部滚动区已管理页面间距，固定底栏只需预留自身高度。 */
    .ant-pro-layout-has-footer {
      margin-block-start: 0 !important;
    }

    @media (prefers-reduced-motion: reduce) {
      .ant-pro-sider-fixed,
      div:has(+ .ant-pro-sider-fixed) {
        /* ProLayout 的占位 div 带行内过渡，需同时关闭以免与头部错位。 */
        transition: none !important;
      }
    }

    .ant-pro-sider.ant-layout-sider,
    .ant-pro-sider .ant-layout-sider-children,
    .ant-pro-drawer-sider .ant-drawer-body {
      background: ${token.colorBgContainer};
    }

    .ant-pro-sider .ant-layout-sider-children {
      margin-inline-end: 0;
      padding-inline: 0;
      border-inline-end: 1px solid ${token.colorBorderSecondary};
    }

    .ant-pro-drawer-sider .ant-drawer-body {
      padding-inline: 0;
    }

    .ant-pro-sider-logo {
      height: 56px;
      padding: 0 16px;
      background: ${token.colorBgContainer};
      border-block-end: 0;
    }

    .ant-pro-sider-logo > a,
    .ant-pro-sider-logo > div {
      gap: 8px;
    }

    .ant-pro-sider-logo h1 {
      margin-inline-start: 0;
      font-size: 18px;
      font-weight: 600;
      line-height: 32px;
    }

    @media (max-width: ${token.screenSMMax}px) {
      height: auto;
      min-height: 100dvh;
      overflow: visible;

      /* 内容回弹时露出的背景与工作区同色，避免显示默认渐变的白色起点。 */
      .ant-pro-layout-bg-list {
        background: ${token.colorBgLayout};
      }

      .ant-pro-layout-container {
        flex: 1 1 auto;
      }

      .ant-pro-layout-container > .ant-layout-header:not(.ant-pro-layout-header) {
        /* ProLayout 的固定头部占位高度是行内样式。 */
        height: calc(56px + env(safe-area-inset-top, 0px)) !important;
      }

      .ant-pro-layout-header.ant-layout-header {
        height: calc(56px + env(safe-area-inset-top, 0px));
        padding-block-start: env(safe-area-inset-top, 0px);
        line-height: 56px;
        background: ${token.colorBgContainer};
        border-block-end: 1px solid ${token.colorBorderSecondary};
        /* 保留 ProLayout 的滤镜层，顶栏本身仍使用不透明主题底色。 */
        -webkit-backdrop-filter: blur(8px);
        backdrop-filter: blur(8px);
      }

      .ant-pro-global-header {
        height: 56px;
        gap: ${token.marginXS}px;
        margin: 0;
        padding-inline-end: ${token.padding}px;
      }

      .ant-pro-global-header-collapsed-button {
        display: inline-grid;
        width: 56px;
        height: 56px;
        margin-inline-end: 0;
        place-items: center;
      }

      .ant-pro-global-header-logo-mobile {
        display: none;
      }
    }
  `,
  sidebarMenuSurface: css`
    width: 100%;
    height: 100%;
    background: ${token.colorBgContainer};

    .ant-menu {
      border-inline-end: 0 !important;
    }

    .ant-menu-inline .ant-menu-sub.ant-menu-inline {
      padding-inline-start: 0;
    }
  `,
  headerSidebar: css`
    position: relative;
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    min-width: 0;
    height: 100%;
    overflow: hidden;
    /* 与 ProLayout 固定侧栏及占位元素的宽度过渡保持一致。 */
    transition: width 0.2s ease 0s;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,
  sidebarBrand: css`
    display: flex;
    align-items: center;
    width: 100%;
    height: 56px;
    gap: 8px;
    overflow: hidden;
    white-space: nowrap;

    strong {
      overflow: hidden;
      font-size: 18px;
      font-weight: 600;
      line-height: 32px;
      text-overflow: ellipsis;
    }
  `,
  workspace: css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-width: 0;
    max-width: 100%;
    min-height: 0;
    overflow: hidden;
    background: ${token.colorBgLayout};

    @media (max-width: ${token.screenSMMax}px) {
      flex: 1 1 auto;
      overflow: visible;
      /* 固定标签栏退出文档流，只在这里预留一次高度。 */
      padding-block-start: ${token.controlHeight + token.marginXS}px;
    }

    &[data-admin-shell-fullscreen='true'] {
      position: fixed;
      z-index: ${token.zIndexPopupBase};
      inset: 0;
      width: 100%;
      height: 100dvh;
      min-height: 0;
      overflow: hidden;

      /* 全屏工作区自行容纳底栏，按实际高度占位，兼容手机按钮换行。 */
      > .ant-pro-footer-bar {
        position: static;
        flex-shrink: 0;
        width: 100% !important;
      }
    }
  `,
  pageScroll: css`
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    min-width: 0;
    overflow: auto;

    @media (max-width: ${token.screenSMMax}px) {
      flex: 1 1 auto;
      overflow: visible;
    }
  `,
  pageArea: css`
    flex: 1 0 auto;
    min-height: 0;
    padding: ${token.paddingLG}px;

    &:has([data-chat-layout]) {
      display: flex;
      flex-direction: column;
      flex: 1 0 480px;
    }

    @media (max-width: ${token.screenMDMax}px) {
      min-width: 0;
      max-width: 100%;
      overflow-x: clip;

      .ant-pro-page-container,
      .ant-pro-grid-content,
      .ant-pro-grid-content-children,
      .ant-pro-page-container-children-container {
        min-width: 0;
        max-width: 100%;
      }

      .ant-pro-steps-form-container,
      .ant-pro-steps-form-container form {
        min-width: 0;
        width: 100%;
        max-width: 100%;
      }

      .ant-pro-form > div:not(.ant-pro-form-light-filter) .pro-field {
        width: 100%;
        max-width: 100%;
      }
    }

    @media (max-width: ${token.screenSMMax}px) {
      /* 手机由文档负责纵向滚动，表格自行横向滚动，外层不再重复裁剪。 */
      overflow: visible;
    }

  `,
  tabsWrap: css`
    position: relative;
    z-index: 8;
    flex-shrink: 0;
    height: ${token.controlHeight + token.marginXS}px;
    min-width: 0;
    max-width: 100%;
    padding: 0;
    background: ${token.colorBgContainer};

    @media (max-width: ${token.screenSMMax}px) {
      /* Safari 顶部回弹时，sticky 会离开吸顶位置；标签栏与头部一样固定。 */
      position: fixed;
      inset-inline: 0;
      top: calc(56px + env(safe-area-inset-top, 0px));
    }
    @media (max-width: 767px), (hover: none) and (pointer: coarse) {
      .ant-tabs-tab-remove {
        min-width: 32px;
        min-height: 32px;
        margin-inline-end: 0;
      }
    }
  `,
  tabAction: css`
    --tab-action-hover: transparent;
    line-height: 1;

    &.ant-btn {
      flex: 0 0 44px;
      width: 44px;
      height: ${token.controlHeight + token.marginXS - token.lineWidth}px;
      padding: 0;
      border-radius: 0;
    }

    @media (hover: hover) {
      --tab-action-hover: ${token.colorFillTertiary};
    }
  `,
  sortableTab: css`
    cursor: grab;
    touch-action: none;

    &:active {
      cursor: grabbing;
    }
  `,
  draggingTab: css`
    background: ${token.colorBgElevated};
    box-shadow: ${token.boxShadowTertiary};
  `,
  avatarName: css`
    max-width: 96px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
}));
